/**
 * WO#2 — Authoritative owner-parcels client.
 *
 * Retrieves the COMPLETE set of parcels owned by a wallet from the canonical
 * read endpoint `GET /api/v1/parcels?owner=<owner>` (cursor-paginated).
 *
 * FAIL-CLOSED CONTRACT (WO#2 guard A): this function returns the full owner
 * dataset ONLY if every page succeeds and pagination reaches its authoritative
 * terminal condition (`next_cursor === null`). Any page failure, malformed
 * body, invalid cursor, or abort THROWS — callers must then treat the
 * territory as UNAVAILABLE and never compute capacity from a partial list.
 */
import { API_BASE } from "./api";
import { normalizeOwnerIdentity } from "../domain/territory";

/** Canonical public parcel shape returned by the read API. */
export interface ApiParcel {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  owner: string;
  claim_tx?: string | null;
  claim_block?: number | null;
}

/** Raised whenever the complete owner dataset could not be authoritatively obtained. */
export class OwnerParcelsIncompleteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OwnerParcelsIncompleteError";
  }
}

const PAGE_SIZE = 200; // matches backend PAGE_SIZE_CAP
const MAX_PAGES = 10_000; // hard safety guard against a pathological cursor loop

function isValidApiParcel(p: unknown): p is ApiParcel {
  if (typeof p !== "object" || p === null) return false;
  const r = p as Record<string, unknown>;
  return (
    typeof r.id === "number" &&
    typeof r.x === "number" &&
    typeof r.y === "number" &&
    typeof r.w === "number" &&
    typeof r.h === "number" &&
    typeof r.owner === "string"
  );
}

/**
 * Fetch ALL parcels owned by `owner`, following pagination to completion.
 * Throws `OwnerParcelsIncompleteError` (fail-closed) on any incomplete state.
 */
export async function fetchAllOwnerParcels(
  owner: string,
  signal?: AbortSignal,
  maxPages: number = MAX_PAGES,
): Promise<ApiParcel[]> {
  // Single canonical frontend identity law (WO#1). No parallel .toLowerCase().
  const normalized = normalizeOwnerIdentity(owner);
  const collected: ApiParcel[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;
  let pages = 0;

  do {
    if (++pages > maxPages) {
      throw new OwnerParcelsIncompleteError("owner parcels pagination exceeded safety guard");
    }
    const params = new URLSearchParams({
      owner: normalized,
      page_size: String(PAGE_SIZE),
    });
    if (cursor) params.set("cursor", cursor);

    let resp: Response;
    try {
      resp = await fetch(`${API_BASE}/api/v1/parcels?${params.toString()}`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal,
      });
    } catch (e) {
      throw new OwnerParcelsIncompleteError(
        `owner parcels request failed: ${(e as Error).message}`,
      );
    }
    if (!resp.ok) {
      throw new OwnerParcelsIncompleteError(`owner parcels page HTTP ${resp.status}`);
    }

    let body: unknown;
    try {
      body = await resp.json();
    } catch {
      throw new OwnerParcelsIncompleteError("owner parcels page: malformed JSON");
    }
    const b = body as { parcels?: unknown; next_cursor?: unknown } | null;
    if (!b || !Array.isArray(b.parcels)) {
      throw new OwnerParcelsIncompleteError("owner parcels page: malformed shape");
    }
    for (const p of b.parcels) {
      if (!isValidApiParcel(p)) {
        throw new OwnerParcelsIncompleteError("owner parcels page: malformed parcel");
      }
      collected.push(p);
    }

    const nc = b.next_cursor;
    if (nc === null || nc === undefined) {
      cursor = null;
    } else if (typeof nc !== "string") {
      throw new OwnerParcelsIncompleteError("owner parcels page: invalid next_cursor");
    } else if (nc.trim() === "") {
      // A backend continuation cursor is always a non-empty string. A blank
      // string is malformed and MUST NOT be treated as terminal success.
      throw new OwnerParcelsIncompleteError("owner parcels page: blank next_cursor");
    } else if (seenCursors.has(nc)) {
      // Fail FAST on a pathological repeated cursor rather than looping to maxPages.
      throw new OwnerParcelsIncompleteError("owner parcels pagination cursor repeated (loop)");
    } else {
      seenCursors.add(nc);
      cursor = nc;
    }
  } while (cursor);

  return collected;
}
